import { useState } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import {
  getCareProviderSearchType,
  POINT_OF_CARE_VALUES,
} from "@/modules/patients/agenda/time-window/lib/get-search-type"
import { isEmergencyReason, TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"
import { SpecialtySelect, SpecialtyType } from "@/modules/patients/detail/progress-note/components/specialty-select"
import { ProgressNoteSelect } from "@/modules/patients/detail/progress-note/point-of-care/components/progress-note-select"
import { usePointOfCareContext } from "@/modules/patients/detail/progress-note/point-of-care/point-of-care-context"
import { PointOfCareType } from "@/types/__generated__/union-types"

interface ReferralToFieldsetProps {
  isViewMode: boolean
  isEligibleForPreventionProgram: boolean
}

export const ReferralToFieldset = ({ isViewMode, isEligibleForPreventionProgram }: ReferralToFieldsetProps) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<TimeWindowFormSchema>()

  const watchedReferralReason = useWatch({ control, name: "referralReason" })
  const isCurrentReasonEmergency = isEmergencyReason(watchedReferralReason)

  const { pointOfCareItems } = usePointOfCareContext()
  const [pointOfCare, setPointOfCare] = useState<PointOfCareType>()
  const [specialtyName, setSpecialtyName] = useState<SpecialtyType | null>(null)
  const hasSpecialtySelection = pointOfCare === "SPECIALIST" || pointOfCare === "HOSPITAL_ELECTIVE"

  const searchType = getCareProviderSearchType(pointOfCare, !!specialtyName)

  return (
    <fieldset className="flex flex-col gap-3" data-testid="referral-to-care-provider">
      <legend className="text-on-surface mb-3 text-base font-medium" data-testid="referral-to-legend">
        {t("patient.agenda.timeWindowForm.referralTo")}
      </legend>
      {isEligibleForPreventionProgram && (
        <Controller
          name="involvesPreventionProgram"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DetailCheckbox
              className="w-fit"
              data-testid="involves-prevention-program-checkbox"
              checked={value}
              onCheckedChange={onChange}
              disabled={isViewMode}>
              {t("patient.agenda.timeWindowForm.involvesPreventionProgram")}
            </DetailCheckbox>
          )}
        />
      )}
      <ProgressNoteSelect
        label={t("episodeOfCare.progressNote.pointOfCare.medicalPointOfCare")}
        options={pointOfCareItems.filter((item) => POINT_OF_CARE_VALUES.includes(item.value))}
        testId="medical-point-of-care"
        defaultValue={pointOfCare}
        isClearable
        disabled={isViewMode}
        onSelect={(value: PointOfCareType | undefined) => {
          setPointOfCare(value)
          setValue("referralToCareProviderId", "", { shouldValidate: true })
          setSpecialtyName(null)
        }}
      />
      {hasSpecialtySelection && (
        <SpecialtySelect
          defaultValue={specialtyName?.word ?? ""}
          onSelect={setSpecialtyName}
          required={false}
          onClear={() => setSpecialtyName(null)}
          disabled={isViewMode}
        />
      )}
      <Controller
        name="referralToCareProviderId"
        control={control}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <CareProviderSearch
            key={searchType}
            label={`${t("patient.agenda.timeWindowForm.careProvider")}${!isCurrentReasonEmergency ? "*" : ""}`}
            searchType={searchType}
            name={name}
            value={value}
            analyticsSectionName="timewindow_referralto"
            disabled={isViewMode}
            onSelect={onChange}
            onRemove={() => onChange("")}
            error={t(error?.message || "")}
            allowUnknownCareProvider={true}
            specialtyId={specialtyName?.specialty?.id}
          />
        )}
      />
      <Controller
        name="nameOfDoctor"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            label={t("patient.agenda.timeWindowForm.nameOfDoctor")}
            error={t(error?.message || "")}
            disabled={isViewMode}
          />
        )}
      />
    </fieldset>
  )
}
